import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faRobot } from '@fortawesome/free-solid-svg-icons';
import Lottie from 'react-lottie-player';
import AnalysisSteps from './AnalysisSteps/AnalysisSteps';
import './PatientLongevityAI.css';

const API_BASE_URL = 'https://service9008.szapfs.org'; // Or 'http://localhost:9008'

function PatientLongevityAI({ isOpen, onClose, userData }) {
    const [lottieData, setLottieData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [steps, setSteps] = useState([]);
    const [logs, setLogs] = useState([]);
    const [activeStep, setActiveStep] = useState(null);

    useEffect(() => {
        // Use native Fetch API for all HTTP requests as per project requirement
        fetch('https://lottie.host/e225fcd9-4a02-4001-a8f5-b67e21e1bc42/nAt6uKG3C5.json')
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            console.log("Lottie data loaded successfully");
            setLottieData(data);
        })
        .catch(error => {
            console.error("Could not load Lottie animation:", error);
        });
    }, []);

    useEffect(() => {
        console.log("Analysis useEffect triggered. isOpen:", isOpen, "userData:", !!userData, "User_ID:", userData?.User_ID);

        const controller = new AbortController();
        const signal = controller.signal;

        const addLog = (message) => {
            const timestamp = new Date().toLocaleTimeString();
            setLogs(prevLogs => [...prevLogs, `[${timestamp}] ${message}`]);
            console.log(`[${timestamp}] ${message}`);
        };

        const fetchAnalysis = async (userId) => {
            setLoading(true);
            setSteps([]);
            setLogs([]); // Start with fresh logs for this run

            addLog(`Initiating analysis for User ${userId}...`);

            try {
                const fetchStep = async (stepNumber) => {
                    if (signal.aborted) throw new Error('Aborted');
                    setActiveStep(stepNumber);
                    addLog(`Requesting Step ${stepNumber}...`);
                    const targetUrl = `${API_BASE_URL}/analysis/${userId}/step${stepNumber}`;
                    console.log(">>> FETCHING URL:", targetUrl);
                    const response = await fetch(targetUrl, { signal });

                    if (signal.aborted) throw new Error('Aborted');

                    let stepData; // Declare stepData outside the try block

                    if (!response.ok) {
                        // ... (error handling for !response.ok remains the same) ...
                        let errorBody = `HTTP ${response.status} ${response.statusText}`;
                        try {
                             const errorJson = await response.json();
                             errorBody += `: ${errorJson.error || JSON.stringify(errorJson)}`;
                        } catch (e) { /* ignore */ }
                        throw new Error(`Step ${stepNumber} failed: ${errorBody}`);
                    }

                    // --- Try to parse JSON ---
                    try {
                        stepData = await response.json(); // Assign parsed data

                        // --- IMMEDIATE LOGGING AFTER PARSE ---
                        console.log(`****** RAW stepData RECEIVED for Step ${stepNumber} ******`);
                        console.log("Type:", typeof stepData);
                        // Use console.dir for better object inspection in browser devtools
                        console.dir(stepData);
                        console.log("Stringified:", JSON.stringify(stepData)); // Log stringified version too
                        console.log(`****** END RAW stepData for Step ${stepNumber} ******`);
                        // --- END IMMEDIATE LOGGING ---

                    } catch (jsonError) {
                         // Handle cases where response.ok is true but body is not valid JSON
                         console.error(`****** ERROR Parsing JSON for Step ${stepNumber} ******`, jsonError);
                         // Attempt to read the raw text response if JSON parsing fails
                         try {
                            const textResponse = await response.text(); // Need new await as body can only be read once
                            console.error(`****** RAW Text Response Body for Step ${stepNumber}: ${textResponse.substring(0, 500)}... ******`);
                         } catch (textError) {
                             console.error(`****** Failed to read text body for Step ${stepNumber} after JSON error ******`, textError);
                         }
                         throw new Error(`Step ${stepNumber} response was OK, but failed to parse JSON: ${jsonError.message}`);
                    }


                    // --- Original Logging & State Update ---
                    // Now access stepData.title *after* inspecting the raw stepData
                    addLog(`Received Step ${stepNumber}: ${stepData?.title}`); // Use optional chaining just in case stepData is null/undefined somehow
                    setSteps(prevSteps => [...prevSteps, stepData]);
                };

                await fetchStep(1);
                await fetchStep(2);
                await fetchStep(3);
                await fetchStep(4);

                addLog("Analysis sequence completed successfully.");
                setActiveStep(null); // Clear active step when done

            } catch (error) {
                if (error.name === 'AbortError') {
                    addLog("Analysis sequence aborted.");
                } else {
                    console.error('Error during analysis sequence:', error);
                    addLog(`Error during analysis: ${error.message}`);
                }
            } finally {
                 // Check signal before setting loading to false in case of race condition with unmount
                if (!signal.aborted) {
                   setLoading(false);
                   setActiveStep(null); // Clear active step
                }
            }
        };

        if (isOpen && userData && userData.User_ID) {
            console.log("Condition met, starting fetchAnalysis.");
            fetchAnalysis(userData.User_ID);
        } else {
            console.log("Condition NOT met, resetting state.");
            // Reset if opened without user data, or when closed
            setLoading(false);
            setSteps([]);
            setLogs([]);
        }

        return () => {
            console.log("Cleanup function running: Aborting fetch.");
            controller.abort();
        };

    }, [isOpen, userData]); // Dependencies: effect runs when these change


    const handleOutsideClick = (e) => {
        // Close if the click is on the container itself (the background)
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return ReactDOM.createPortal(
        <div 
            className={`patient-longevity-ai ${isOpen ? 'open' : ''}`}
            onClick={handleOutsideClick} // Attach the new handler here
        >
            <div className="patient-longevity-ai-content">
                <button className="patient-longevity-ai-close" onClick={onClose}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
                <div className="patient-longevity-ai-header">
                    {lottieData && (
                        <Lottie
                            loop
                            animationData={lottieData}
                            play={isOpen} // Always play when modal is open
                            className="patient-longevity-ai-lottie"
                        />
                    )}
                </div>

                <div className="patient-longevity-ai-body">
                    {/* Use the new AnalysisSteps component */}
                    <AnalysisSteps 
                        steps={steps} 
                        loading={loading} 
                        activeStep={activeStep}
                    />
                    
                    {/* Conversation Section */}
                    <div className="pai-conversation-container">
                        <div className="pai-conversation-messages">
                            {/* Dummy conversation data */}
                            <div className="pai-message pai-system-message">
                                <span className="pai-message-content">Hello! I've analyzed your health data. What would you like to know?</span>
                            </div>
                            <div className="pai-message pai-user-message">
                                <span className="pai-message-content">Can you tell me about my sleep patterns?</span>
                            </div>
                            <div className="pai-message pai-system-message">
                                <span className="pai-message-content">Based on your data, your average sleep duration is 7.2 hours with 1.5 hours of deep sleep. This is slightly below the recommended amount for your age group.</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="patient-longevity-ai-body-chatinput"></div>
            </div>
        </div>,
        document.body
    );
}

export default PatientLongevityAI;